/**
 * DECISIONS 300 (69.70 step 2, the switch audit's leans; the owner: "Take the leans"): the eighteen switches flipped on in
 * the one default game — on by default on the action clock, on unless false (a count: its number unless set); the classic
 * turn game's only when set (it stays frozen). 69.39's `zonesOn` and 69.65's `blastModelOn` read the same way. The Lab's
 * rows (src/ui/labRows.ts) default on, the setup passes off as given, and `systems.ts` states the absent value as on, so the
 * four places a default lives agree (ARCHITECTURE §5.33 step 4). Of the eighteen, the dump arm is drawn only (no setup
 * field) and the airstrikes are a per-team count the setup issues (`AIRSTRIKES_BY_DEFAULT`); the rest read here.
 * 63.19 step 3 (DECISIONS 332): `vehicleImpact` (a moving vehicle striking a person, and the bots keeping out of its way) joins them.
 */
import type { GameConfig } from '../types';

/** The flipped switches a match reads from its config. */
export type DefaultOnSwitch = 'smokeSight' | 'jumpFeel' | 'localSteer' | 'realTiming' | 'shipPickup' | 'hitLocations' | 'suppression'
  | 'gl40Stock' | 'motorPool' | 'crewmanRole' | 'countermeasures' | 'pointDefence' | 'shipTurrets' | 'vehicleImpact';
/** The flipped counts: each side's ships of a kind. */
export type DefaultCount = 'gunships' | 'helicopters';

/** Each side's ships by default on the action clock (300: one a side, the audit's lean). */
export const COUNTS_BY_DEFAULT: Readonly<Record<DefaultCount, number>> = { gunships: 1, helicopters: 1 };
/** Each side's airstrikes by default on the action clock (300: three, the towed guns' three-round salvo; the Lab's row). */
export const AIRSTRIKES_BY_DEFAULT = 3;

/**
 * Every flipped switch turned off in a GameConfig (false, the ships 0): a measuring harness's old world — the action range
 * (ai/actionRange.ts) — and the tests' (tests/helpers/flipsOff.ts). A setup turns off the motor pool and the airstrikes too.
 */
export const CONFIG_FLIPS_OFF = {
  smokeSight: false, jumpFeel: false, localSteer: false, realTiming: false, shipPickup: false, hitLocations: false, suppression: false,
  gl40Stock: false, crewmanRole: false, countermeasures: false, pointDefence: false, towedGuns: false, shipTurrets: false, vehicleImpact: false,
  gunships: 0, helicopters: 0,
} as const satisfies Partial<GameConfig>;

type Cfg = Partial<Pick<GameConfig, DefaultOnSwitch | DefaultCount | 'towedGuns' | 'clock'>> | undefined;

/** Whether `key` is on: set true, or on the action clock and not set false. */
export function defaultOn(config: Cfg, key: DefaultOnSwitch): boolean {
  const v = config?.[key];
  return v === true || (config?.clock === 'action' && v !== false);
}

/** 69.13 (a), DECISIONS 233 ("Can link"): the towed guns, with the point defence only. */
export function towedGunsOn(config: Cfg): boolean {
  if (!defaultOn(config, 'pointDefence')) return false;
  return config?.towedGuns === true || (config?.clock === 'action' && config.towedGuns !== false);
}

/** Each side's ships of `key`: the number set, or on the action clock `COUNTS_BY_DEFAULT`'s; 0 for none. */
export function countOf(config: Cfg, key: DefaultCount): number {
  const v = config?.[key];
  return Math.max(0, Math.floor(v ?? (config?.clock === 'action' ? COUNTS_BY_DEFAULT[key] : 0)));
}
